'use client';

import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { CheckCircle2, XCircle, ArrowRight, Sparkles, BarChart3, MessageSquare, Zap } from 'lucide-react';
import Link from 'next/link';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';

export default function PlotsAlotVsExcelPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-emerald-600 bg-clip-text text-transparent">
              PlotsAlot vs Excel
            </h1>
            <p className="text-xl text-muted-foreground mb-8">Excel Alternative for Modern Data Analysis</p>
            <p className="text-lg text-foreground/80 mb-8 leading-relaxed">
              Excel is the world's most popular spreadsheet tool. PlotsAlot brings AI-powered data analysis with natural
              language. Here's the complete comparison.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                href="/auth/signin"
                className="inline-flex items-center px-6 py-3 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors">
                Start Free Trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <Link
                href="/pricing"
                className="inline-flex items-center px-6 py-3 rounded-lg border border-border hover:bg-accent transition-colors">
                See Pricing
              </Link>
            </div>
          </motion.div>
        </section>

        {/* Key Differences Section */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border rounded-xl p-6">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                <MessageSquare className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Natural Language Queries</h3>
              <p className="text-muted-foreground text-sm">
                Ask questions in plain English: "What's the average sales by region?" Get instant answers and
                visualizations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card border border-border rounded-xl p-6">
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-4">
                <Sparkles className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">AI-Powered Analysis</h3>
              <p className="text-muted-foreground text-sm">
                Automatic insights, pattern detection, and recommendations. AI suggests relevant analyses and
                visualizations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-card border border-border rounded-xl p-6">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Instant Visualizations</h3>
              <p className="text-muted-foreground text-sm">
                Create publication-quality charts in seconds through conversation. No manual chart configuration needed.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Pricing Comparison */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Real Pricing Comparison</h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Excel Pricing */}
            <div className="bg-card border border-border rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-2">Microsoft Excel</h3>
              <p className="text-3xl font-bold text-primary mb-4">$7-20/month</p>
              <p className="text-muted-foreground mb-6">
                Microsoft 365 Personal: $7/mo. Family: $10/mo. Business: $12-20/user/mo. Desktop only options available.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Spreadsheet editor</p>
                    <p className="text-sm text-muted-foreground">Industry standard interface</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Formulas & functions</p>
                    <p className="text-sm text-muted-foreground">500+ built-in functions</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Pivot tables</p>
                    <p className="text-sm text-muted-foreground">Manual configuration required</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Desktop + cloud</p>
                    <p className="text-sm text-muted-foreground">Desktop app + web version</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-red-500 mt-1">✗</div>
                  <div>
                    <p className="font-medium">AI conversation</p>
                    <p className="text-sm text-muted-foreground">Manual analysis only</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-red-500 mt-1">✗</div>
                  <div>
                    <p className="font-medium">Python execution</p>
                    <p className="text-sm text-muted-foreground">Limited Python integration</p>
                  </div>
                </div>
              </div>
            </div>

            {/* PlotsAlot Pricing */}
            <div className="bg-card border-2 border-primary rounded-xl p-8 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-1 rounded-full text-sm font-medium">
                Modern AI Alternative
              </div>
              <h3 className="text-2xl font-bold mb-2">PlotsAlot</h3>
              <p className="text-3xl font-bold text-primary mb-4">$0-10/month</p>
              <p className="text-muted-foreground mb-6">
                Free: 3 messages. Pro: $10/mo (200 messages). All plans include Python & AI.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Natural language interface</p>
                    <p className="text-sm text-muted-foreground">Chat with your data</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">AI-powered insights</p>
                    <p className="text-sm text-muted-foreground">Automatic pattern detection</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Python code execution</p>
                    <p className="text-sm text-muted-foreground">Full data science stack</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Instant visualizations</p>
                    <p className="text-sm text-muted-foreground">Publication-quality charts</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-green-500 mt-1">✓</div>
                  <div>
                    <p className="font-medium">Google Analytics integration</p>
                    <p className="text-sm text-muted-foreground">Direct data connection</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-red-500 mt-1">✗</div>
                  <div>
                    <p className="font-medium">Manual cell editing</p>
                    <p className="text-sm text-muted-foreground">Conversation-based workflow</p>
                  </div>
                </div>
              </div>

              <Link
                href="/auth/signin"
                className="block w-full text-center py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
                Start Free Trial
              </Link>
            </div>
          </div>
        </section>

        {/* Feature Comparison Table */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <h2 className="text-3xl font-bold text-center mb-12">Full Feature Breakdown</h2>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-card rounded-xl overflow-hidden">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-4 font-semibold">Feature</th>
                  <th className="text-center p-4 font-semibold">PlotsAlot</th>
                  <th className="text-center p-4 font-semibold">Excel</th>
                  <th className="text-left p-4 font-semibold">Winner & Why</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="p-4 font-medium">Natural Language Queries</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Ask "What's the monthly trend?"
                    and get instant answers. Excel requires formulas.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Learning Curve</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Low</span>
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Moderate-High</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Natural language means anyone can
                    analyze data. Excel requires formula knowledge.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">AI-Powered Insights</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - AI suggests analyses and finds
                    patterns automatically. Excel requires manual exploration.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Python Code Execution</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Limited</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Full Python environment with
                    pandas, plotly, scikit-learn. Excel has basic Python integration.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Statistical Analysis</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Tie</span> - Both support regression, forecasting.
                    PlotsAlot via Python, Excel via built-in tools.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Visualization Quality</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Modern, publication-quality
                    charts via Plotly/Matplotlib. Excel charts are functional but dated.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Chart Creation Speed</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Manual</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Instant charts via conversation.
                    Excel requires data selection, chart wizard, formatting.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Manual Cell Editing</td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Excel</span> - Full spreadsheet interface for manual
                    data entry and editing.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Formulas & Functions</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Via Python</span>
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Excel</span> - 500+ built-in functions. PlotsAlot
                    uses Python for calculations.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Pivot Tables</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Via Chat</span>
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Excel</span> - Native pivot tables. PlotsAlot
                    creates equivalent analysis via conversation.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Large Dataset Handling</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Limited</span>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Handles millions of rows via
                    pandas. Excel slows down after ~100k rows.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Google Analytics Integration</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - Direct OAuth connection to GA4.
                    Excel requires manual CSV exports.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Macros & Automation</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Via Python</span>
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Excel</span> - VBA macros for automation. PlotsAlot
                    uses Python scripts.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Collaboration</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Chat Sharing</span>
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Excel</span> - Real-time co-authoring in Excel
                    Online. PlotsAlot has chat sharing.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Offline Access</td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Excel</span> - Desktop app works offline. PlotsAlot
                    requires internet connection.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Mobile Support</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Web only</span>
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Excel</span> - Native iOS/Android apps. PlotsAlot is
                    mobile-responsive web only.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">File Format Support</td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">PlotsAlot</span> - CSV, Excel, JSON, Parquet, PDF.
                    Excel reads most formats but optimized for .xlsx.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Export Options</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">PNG, Code</span>
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Excel</span> - Export to PDF, CSV, HTML, XML.
                    PlotsAlot exports charts and Python code.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Templates</td>
                  <td className="p-4 text-center">
                    <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Excel</span> - Extensive template library for
                    budgets, calendars, trackers.
                  </td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Enterprise Features</td>
                  <td className="p-4 text-center">
                    <span className="text-muted-foreground text-sm">Growing</span>
                  </td>
                  <td className="p-4 text-center">
                    <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Excel</span> - SharePoint integration, Power Query,
                    Power Pivot, advanced security.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Understanding the Difference */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Understanding the Difference</h2>

            <div className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-muted-foreground leading-relaxed mb-6">
                This comparison requires context:{' '}
                <strong className="text-foreground">
                  Excel and PlotsAlot represent different paradigms of data work
                </strong>
                .
              </p>

              <div className="bg-card border border-border rounded-xl p-6 mb-6">
                <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <span className="text-green-500 font-bold">E</span>
                  </div>
                  Microsoft Excel
                </h3>
                <p className="text-muted-foreground mb-0">
                  Excel is a <strong className="text-foreground">spreadsheet application</strong> with 30+ years of
                  development. It provides a grid interface for manual data entry, formulas for calculations, charts for
                  visualization, and pivot tables for analysis. Excel requires you to know formulas, understand cell
                  references, and manually configure most features. It's the industry standard for financial modeling,
                  budgets, and structured data work.
                </p>
              </div>

              <div className="bg-card border border-border rounded-xl p-6 mb-6">
                <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg gradient-bg flex items-center justify-center">
                    <BarChart3 className="h-4 w-4 text-white" />
                  </div>
                  PlotsAlot
                </h3>
                <p className="text-muted-foreground mb-0">
                  PlotsAlot is a <strong className="text-foreground">conversational data analysis platform</strong>. You
                  chat with your data using natural language. Upload files, ask questions like "Show me monthly trends"
                  or "What's the correlation between these columns?", and get instant answers with visualizations. No
                  formulas, no cell references, no manual chart configuration. The AI understands intent and generates
                  appropriate analyses automatically using Python.
                </p>
              </div>

              <p className="text-lg font-semibold text-foreground mt-8 mb-4">
                The key difference: Excel is a manual spreadsheet tool. PlotsAlot is AI-powered conversational analysis.
              </p>
            </div>
          </div>
        </section>

        {/* When to Choose - Comprehensive Guide */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Choose PlotsAlot */}
            <div className="bg-card border-2 border-primary rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-primary" />
                When to Choose PlotsAlot
              </h3>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-2">1. You Want to Chat with Data</h4>
                  <p className="text-muted-foreground text-sm">
                    If natural language feels more intuitive than formulas, PlotsAlot transforms how you work. Ask "What
                    are the top 5 products?" instead of writing SUMIF formulas.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">2. You're New to Data Analysis</h4>
                  <p className="text-muted-foreground text-sm">
                    No Excel expertise needed. PlotsAlot understands plain English questions. Perfect for marketers,
                    PMs, founders who need insights without technical training.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">3. You Work with Large Datasets</h4>
                  <p className="text-muted-foreground text-sm">
                    PlotsAlot handles millions of rows efficiently via pandas. Excel becomes sluggish above 100k rows.
                    Perfect for analyzing large CSV exports.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">4. You Need Quick Exploratory Analysis</h4>
                  <p className="text-muted-foreground text-sm">
                    Upload a file, ask questions, get instant visualizations. Faster than manually creating pivot tables
                    and charts in Excel.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">5. You Want Modern, Beautiful Charts</h4>
                  <p className="text-muted-foreground text-sm">
                    Publication-quality visualizations via Plotly and Matplotlib. No manual formatting needed - AI
                    generates polished charts automatically.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">6. You Use Google Analytics</h4>
                  <p className="text-muted-foreground text-sm">
                    Direct GA4 integration via OAuth. Analyze traffic data through conversation. Excel requires manual
                    CSV exports.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">7. You Need Advanced Statistical Analysis</h4>
                  <p className="text-muted-foreground text-sm">
                    Full Python data science stack with scikit-learn for machine learning, statsmodels for statistics.
                    More powerful than Excel's Analysis ToolPak.
                  </p>
                </div>
              </div>
            </div>

            {/* Choose Excel */}
            <div className="bg-card border border-border rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
                When to Stick with Excel
              </h3>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-2">1. You Need Manual Data Entry</h4>
                  <p className="text-muted-foreground text-sm">
                    Excel excels at cell-by-cell data entry and editing. Perfect for budgets, trackers, and manual
                    record-keeping. PlotsAlot is analysis-focused, not data entry.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">2. Your Workflow Relies on Formulas</h4>
                  <p className="text-muted-foreground text-sm">
                    If your work requires complex nested formulas (VLOOKUP, INDEX/MATCH, array formulas), Excel's
                    formula system is unmatched. PlotsAlot uses Python instead.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">3. You're Building Financial Models</h4>
                  <p className="text-muted-foreground text-sm">
                    Excel is the industry standard for financial modeling, DCF analysis, and scenario planning. The grid
                    interface is essential for building interconnected models.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">4. You Need Offline Access</h4>
                  <p className="text-muted-foreground text-sm">
                    Excel desktop app works without internet. Essential for working on planes, remote locations, or
                    secure environments. PlotsAlot requires connection.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">5. You Have Complex Macros/VBA</h4>
                  <p className="text-muted-foreground text-sm">
                    Existing Excel files with VBA macros, custom functions, and automation scripts. Migration to
                    PlotsAlot would require rewriting in Python.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">6. Team Already Knows Excel</h4>
                  <p className="text-muted-foreground text-sm">
                    If your team has Excel expertise and established workflows, switching tools requires training. Excel
                    familiarity is universal.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-2">7. You Need Enterprise Integration</h4>
                  <p className="text-muted-foreground text-sm">
                    Excel integrates with SharePoint, Power BI, Power Query, Microsoft 365 ecosystem. Essential for
                    large organizations with Microsoft infrastructure.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Use Both Together */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-primary/10 to-emerald-500/10 rounded-2xl p-8 border border-primary/20">
              <h2 className="text-2xl font-bold mb-6 text-center">Pro Tip: Use Both Together</h2>

              <p className="text-muted-foreground mb-6 text-center">
                Many professionals use Excel AND PlotsAlot for different parts of their workflow:
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-card rounded-xl p-6">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">📊</span>
                    Use Excel for:
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Data entry and manual record-keeping</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Building financial models and budgets</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Creating structured templates for teams</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Offline work and mobile access</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-card rounded-xl p-6">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <span className="text-2xl">🤖</span>
                    Use PlotsAlot for:
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Quick exploratory analysis of exports</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Creating presentation-quality visualizations</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Advanced statistical analysis and ML</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>Google Analytics integration and insights</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-6 p-4 bg-card rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  <strong className="text-foreground">Example workflow:</strong> Maintain data in Excel → Export CSV →
                  Analyze in PlotsAlot → Generate charts for reports
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="container mx-auto px-4 md:px-8 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h2>

            <Accordion type="single" className="space-y-2">
              <AccordionItem value="item-1" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Can PlotsAlot replace Excel completely?</AccordionTrigger>
                <AccordionContent>
                  For analysis: yes. For data entry, budgets, financial models: no. PlotsAlot is designed for analyzing
                  existing data, not manual data entry or spreadsheet-style work. Most users keep Excel for some tasks
                  and use PlotsAlot for analysis.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Can I upload Excel files to PlotsAlot?</AccordionTrigger>
                <AccordionContent>
                  Yes! PlotsAlot reads .xlsx and .xls files directly. Upload your Excel spreadsheets and analyze them
                  through conversation. Charts and formulas won't transfer, but data will.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Do I need to know Python to use PlotsAlot?</AccordionTrigger>
                <AccordionContent>
                  No. PlotsAlot generates Python code automatically based on your natural language questions. You can
                  view the code if you're curious, but it's not required. Just chat with your data.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Is PlotsAlot harder to learn than Excel?</AccordionTrigger>
                <AccordionContent>
                  PlotsAlot is actually easier for beginners. No formulas, no cell references, no pivot table
                  configuration. Just ask questions in plain English. Excel has a steep learning curve for advanced
                  features.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Can PlotsAlot handle financial modeling?</AccordionTrigger>
                <AccordionContent>
                  PlotsAlot can analyze financial data and create projections, but Excel is better for building complex
                  financial models with interconnected assumptions. Use Excel for model building, PlotsAlot for
                  analyzing results.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Which is better for data visualization?</AccordionTrigger>
                <AccordionContent>
                  PlotsAlot creates more modern, publication-quality visualizations via Plotly and Matplotlib. Excel
                  charts are functional but visually dated. PlotsAlot is also much faster - instant charts via
                  conversation vs manual configuration.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Can I export data from PlotsAlot to Excel?</AccordionTrigger>
                <AccordionContent>
                  Currently, PlotsAlot exports charts (PNG) and Python code. For data export, you'd download the
                  original file you uploaded. Feature roadmap includes CSV export of analysis results.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8" className="bg-card border border-border rounded-xl px-6">
                <AccordionTrigger>Which is more cost-effective?</AccordionTrigger>
                <AccordionContent>
                  PlotsAlot free tier ($0) includes analysis features. Excel requires Microsoft 365 ($7-20/mo). For data
                  analysis only, PlotsAlot Pro ($19/mo) is competitive. For full office suite, Microsoft 365 makes
                  sense.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-primary/10 to-emerald-500/10 rounded-2xl p-12 border border-primary/20">
            <h2 className="text-3xl font-bold mb-4">Experience the Future of Data Analysis</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Try PlotsAlot free. Upload your Excel files and start chatting with your data today.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                href="/auth/signin"
                className="inline-flex items-center px-8 py-4 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors text-lg font-semibold">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                href="/pricing"
                className="inline-flex items-center px-8 py-4 rounded-lg border-2 border-primary hover:bg-primary/5 transition-colors text-lg font-semibold">
                View Pricing
              </Link>
            </div>
            <p className="text-sm text-muted-foreground mt-6">
              Free tier • No credit card required • Upload Excel, CSV, JSON files
            </p>
          </div>
        </section>

        {/* Other Comparisons */}
        <section className="container mx-auto px-4 md:px-8 mt-16">
          <h3 className="text-2xl font-bold mb-6 text-center">Other Comparisons</h3>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link
              href="/compare/gamma"
              className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors">
              <h4 className="font-semibold mb-2">vs Gamma</h4>
              <p className="text-sm text-muted-foreground">AI presentation tool vs data analysis platform</p>
            </Link>
            <Link
              href="/compare/chatgpt"
              className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors">
              <h4 className="font-semibold mb-2">vs ChatGPT</h4>
              <p className="text-sm text-muted-foreground">General AI vs specialized data analysis</p>
            </Link>
            <Link
              href="/compare/powerbi"
              className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-colors">
              <h4 className="font-semibold mb-2">vs Power BI</h4>
              <p className="text-sm text-muted-foreground">Enterprise BI tools vs conversational analytics</p>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
